/**
 * Auth Feature Routes
 */
import type { RouteRecordRaw } from 'vue-router';
import i18next from 'i18next';
import { useAuthStore } from '@/stores/auth.store';

export const authRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      title: () => i18next.t('auth.login'),
      requiresAuth: false,
      layout: 'blank',
    },
  },
  // Shown instead of /login when a session is already active on this
  // device (e.g. an invite email link opened on a shared workstation) —
  // see authGuard's redirect in router/guards.ts.
  {
    path: '/active-session',
    name: 'active-session',
    component: () => import('./views/ActiveSessionView.vue'),
    meta: {
      title: () => i18next.t('auth.activeSession.title'),
      requiresAuth: false,
      layout: 'blank',
    },
    beforeEnter: () => {
      const authStore = useAuthStore();
      return authStore.isAuthenticated ? undefined : { name: 'login' };
    },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: {
      title: () => i18next.t('auth.resetPassword'),
      requiresAuth: false,
      layout: 'blank',
    },
  },
  {
    path: '/no-access',
    name: 'no-access',
    component: () => import('@/views/NoAccessView.vue'),
    meta: {
      title: () => i18next.t('auth.noAccessTitle', 'Access Unavailable'),
      requiresAuth: false,
      layout: 'blank',
    },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: {
      title: () => i18next.t('auth.forgotPasswordTitle'),
      requiresAuth: false,
      layout: 'blank',
    },
  },
  // OIDC callback — handles Cognito redirect with ?code=&state= after IdP auth.
  // Uses blank layout (no sidebar/header) while the token exchange completes.
  {
    path: '/callback',
    name: 'oidc-callback',
    component: () => import('./views/AuthCallbackView.vue'),
    meta: {
      title: () => i18next.t('auth.pageTitle.signingIn'),
      requiresAuth: false,
      layout: 'blank',
    },
  },
  {
    path: '/logout',
    name: 'logout',
    redirect: () => {
      return { name: 'login' };
    },
  },
  {
    path: '/mfa-setup',
    name: 'mfa-setup',
    component: () => import('./views/MfaSetupView.vue'),
    meta: {
      title: () => i18next.t('auth.mfa.setupTitle', 'Secure Your Account'),
      requiresAuth: false,
      layout: 'blank',
    },
    beforeEnter: () => {
      const authStore = useAuthStore();
      const inFlow =
        authStore.challengeData !== null ||
        authStore.mfaSetupRequired ||
        authStore.mfaSetupAccessToken !== null;
      return inFlow ? undefined : { name: 'login' };
    },
  },
  {
    path: '/mfa',
    name: 'mfa-challenge',
    component: () => import('./views/MfaChallengeView.vue'),
    meta: {
      title: () => i18next.t('profile.twoFactorAuth', 'Two-Factor Authentication'),
      requiresAuth: false,
      layout: 'blank',
    },
    beforeEnter: () => {
      const authStore = useAuthStore();
      return authStore.challengeData ? undefined : { name: 'login' };
    },
  },
];
